import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { API_URL } from "../config/config";
import { extractErrorMessage } from "../helpers/error.helper";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "../components/PageContainer";
import { UserRoleEnum } from "../enums/userRole.enum";
import { registerSchema, RegisterFormData } from "../helpers/formValidation";
import { ErrorMessage } from "../components/form/ErrorMessage";
import {
  formBtnPrimaryClasses,
  formInputClasses,
  formLabelClasses,
} from "../styles/formClasses";
import {
  authCardClasses,
  authPageShellClasses,
  centeredPageHeaderClasses,
  pageHeadingClasses,
  pageLeadClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import { authPageClasses, registerPageClasses } from "../styles/pageSpecificClasses";

const Register: React.FC = () => {
    const navigate = useNavigate();
    const [serverError, setServerError] = useState("");
    const [loading, setLoading] = useState(false);

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            displayName: "",
            email: "",
            password: "",
            userRole: UserRoleEnum.Jobseeker,
        },
    });

    const userRole = watch("userRole");

    const onSubmit = async (data: RegisterFormData) => {
        setServerError("");
        setLoading(true);

        try {
            const res = await fetch(`${API_URL}/auth/register`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(data),
            });

            if (!res.ok) {
                let msg = `Registration failed (${res.status})`;
                try {
                    const problem = await res.json();
                    msg = extractErrorMessage(problem, msg);
                } catch { }
                throw new Error(msg);
            }

            navigate("/login");
        } catch (err: any) {
            setServerError(err?.message ?? "Registration failed. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageContainer maxWidth="full" contentClassName={authPageShellClasses}>
            <div className={registerPageClasses.shellWidth}>
                <div className={centeredPageHeaderClasses}>
                    <span className={subtleEyebrowClasses}>Create Account</span>
                    <h1 className={`${registerPageClasses.heading} ${pageHeadingClasses}`}>Join the summer season</h1>
                    <p className={pageLeadClasses}>
                        Find your dream summer job in Greece, or post one for your business.
                    </p>
                </div>

                <div className={`${authCardClasses} ${authPageClasses.authCardSpacing}`}>
                    <h2 className={registerPageClasses.title}>
                        Register Here
                    </h2>

                    <form onSubmit={handleSubmit(onSubmit)} className={registerPageClasses.form}>
                        {serverError && <p className={registerPageClasses.serverError}>{serverError}</p>}

                        <div>
                            <p className={registerPageClasses.roleLabel}>
                                What would you like to do?
                            </p>
                            <div className={registerPageClasses.roleGrid}>
                                <button
                                    type="button"
                                    onClick={() => setValue("userRole", UserRoleEnum.Admin)}
                                    className={`${registerPageClasses.roleCardBase} ${userRole === UserRoleEnum.Admin ? registerPageClasses.roleCardSelected : registerPageClasses.roleCardUnselected}`}
                                >
                                    <div className={registerPageClasses.roleTitle}>
                                        Admin access
                                    </div>
                                    <p className={registerPageClasses.roleCopy}>
                                        Temporary option to create an administrator account.
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setValue("userRole", UserRoleEnum.Employer)}
                                    className={`${registerPageClasses.roleCardBase} ${userRole === UserRoleEnum.Employer ? registerPageClasses.roleCardSelected : registerPageClasses.roleCardUnselected}`}
                                >
                                    <div className={registerPageClasses.roleTitle}>
                                        Post a new job
                                    </div>
                                    <p className={registerPageClasses.roleCopy}>
                                        I&apos;m an employer or business looking to hire staff.
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setValue("userRole", UserRoleEnum.Jobseeker)}
                                    className={`${registerPageClasses.roleCardBase} ${userRole === UserRoleEnum.Jobseeker ? registerPageClasses.roleCardSelected : registerPageClasses.roleCardUnselected}`}
                                >
                                    <div className={registerPageClasses.roleTitle}>
                                        Find a job
                                    </div>
                                    <p className={registerPageClasses.roleCopy}>
                                        I want to apply for summer jobs in Greece.
                                    </p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setValue("userRole", UserRoleEnum.Viewer)}
                                    className={`${registerPageClasses.roleCardBase} ${userRole === UserRoleEnum.Viewer ? registerPageClasses.roleCardSelected : registerPageClasses.roleCardUnselected}`}
                                >
                                    <div className={registerPageClasses.roleTitle}>
                                        Just look around
                                    </div>
                                    <p className={registerPageClasses.roleCopy}>
                                        I&apos;m exploring first and may decide later.
                                    </p>
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className={formLabelClasses}>Name</label>
                            <input
                                {...register("displayName")}
                                type="text"
                                placeholder="Enter your full name"
                                className={formInputClasses}
                            />
                            <ErrorMessage message={errors.displayName?.message} />
                        </div>

                        <div>
                            <label className={formLabelClasses}>Email</label>
                            <input
                                {...register("email")}
                                type="email"
                                placeholder="your.email@example.com"
                                className={formInputClasses}
                                autoComplete="email"
                            />
                            <ErrorMessage message={errors.email?.message} />
                        </div>

                        <div>
                            <label className={formLabelClasses}>Password</label>
                            <input
                                {...register("password")}
                                type="password"
                                placeholder="Enter your password"
                                className={formInputClasses}
                                autoComplete="new-password"
                            />
                            <ErrorMessage message={errors.password?.message} />
                            <p className={registerPageClasses.passwordHint}>
                                At least 12 characters with upper, lower, digit, and symbol.
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`${formBtnPrimaryClasses} ${authPageClasses.disabledButton}`}
                        >
                            {loading ? "Creating account..." : "Register"}
                        </button>
                    </form>

                    <p className={registerPageClasses.loginPrompt}>
                        Already have an account?{" "}
                        <a href="/login" className={registerPageClasses.loginLink}>
                            Login here
                        </a>
                    </p>
                </div>
            </div>
        </PageContainer>
    );
};

export default Register;
